import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_FULL } from "@/components/common";

const STAGES = ["IDEA", "VALIDATE", "BUILD", "REGISTER", "SET UP", "LAUNCH", "GROW"];

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-[#1E2B3C] text-white p-12 relative overflow-hidden">
        <img src="/icon-512.png" alt="" className="absolute -right-24 -bottom-24 w-[420px] opacity-[0.07] rotate-12" />
        <span className="font-display text-2xl font-extrabold">sidebizz</span>
        <div>
          <h2 className="font-display text-4xl lg:text-5xl font-extrabold leading-[1.05]">Tell us where you are.<br /><span className="text-[#1DB99A]">We'll guide what's next.</span></h2>
          <div className="mt-10 flex flex-wrap gap-2">
            {STAGES.map((s, i) => <span key={s} className="sb-chip bg-white/10 text-white/80" style={{ animationDelay: `${i * 60}ms` }}>{s}</span>)}
          </div>
        </div>
        <p className="text-sm text-white/50">The business-launch companion for Mauritius.</p>
      </div>
      <div className="flex flex-col justify-center px-6 py-10 sm:px-12 pt-safe">
        <div className="w-full max-w-sm mx-auto fade-up">
          <img src={LOGO_FULL} alt="sidebizz" className="h-9 mb-10" />
          <h1 className="text-3xl font-extrabold">{title}</h1>
          <p className="text-[#5C6B73] mt-2 mb-8 text-sm">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold text-[#5C6B73]">{label}</Label>
      <Input id={id} className="h-12 rounded-xl bg-white border-[#EADEC9] text-base" {...props} />
    </div>
  );
}

export function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      signIn(data);
      nav(loc.state?.from || "/", { replace: true });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" subtitle="Continue building your business.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="email" label="Email" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <Field id="password" label="Password" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        <button className="sb-btn-primary w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" /></button>
      </form>
      <p className="text-sm text-[#5C6B73] mt-6">New to Sidebizz? <Link to="/register" className="font-semibold text-[#179B80]" data-testid="goto-register-link">Create a free account</Link></p>
    </Shell>
  );
}

export function Register() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const [first, ...rest] = f.full_name.trim().split(/\s+/);
    if (!first) { toast.error("Please type your full name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { first_name: first, last_name: rest.join(" "), email: f.email, password: f.password, language: "en" });
      signIn(data);
      nav("/", { replace: true });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your free account" subtitle="It’s free. Get 1 full business idea right away — no payment needed.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field id="fn" label="Full name" required autoComplete="name" value={f.full_name} onChange={set("full_name")} data-testid="register-full-name-input" />
        <Field id="em" label="Email address" type="email" required autoComplete="email" value={f.email} onChange={set("email")} data-testid="register-email-input" />
        <Field id="pw" label="Password (at least 8 letters or numbers)" type="password" required minLength={8} autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="register-password-input" />
        <button className="sb-btn-primary w-full min-h-[52px] text-base" disabled={busy} data-testid="register-submit-btn">{busy ? "Creating…" : "Create My Free Account"} <ArrowRight className="h-4 w-4" /></button>
      </form>
      <p className="text-sm text-[#5C6B73] mt-6">Already have an account? <Link to="/login" className="font-semibold text-[#179B80]" data-testid="goto-login-link">Sign in</Link></p>
    </Shell>
  );
}

export function SetupPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  useEffect(() => {
    api.get(`/auth/token/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      signIn(data);
      toast.success("Password set — welcome to Sidebizz!");
      nav("/", { replace: true });
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <Shell title={info?.kind === "reset" ? "Reset your password" : "Set up your account"} subtitle={info ? `For ${info.email}` : "Checking your link…"}>
      {err ? <p className="text-sm text-[#E05A47]" data-testid="setup-password-error">{err}</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="setup-password-form">
          <Field id="np" label="New password (min. 8 characters)" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="setup-password-input" />
          <button className="sb-btn-primary w-full" disabled={!info} data-testid="setup-password-submit-btn">Save password</button>
        </form>
      )}
    </Shell>
  );
}
